/**
 * Reading YouTube URLs: which ones point at a video, and how to embed them.
 *
 * Content hrefs come from the pipeline, so they arrive in every shape YouTube hands
 * out — watch links, share links, shorts, embeds and live URLs. One matcher here
 * keeps the ingestion form's validation and the in-page player agreeing on what
 * counts as a video.
 */

/**
 * A YouTube video URL, capturing the video ID.
 *
 * Mirrors ALLOWED_YOUTUBE_HOSTS and extract_video_id() in
 * server/pipeline_ingestion/. Video IDs are exactly 11 characters of [A-Za-z0-9_-];
 * the trailing guard stops a longer id-like run from matching its first 11.
 */
const YOUTUBE_VIDEO_URL =
  /^https?:\/\/(?:(?:www\.|m\.|music\.)?(?:youtube\.com|youtube-nocookie\.com)\/(?:watch\?(?:[^#]*&)?v=|embed\/|v\/|shorts\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{11})(?![A-Za-z0-9_-])/;

/** `90`, `1m30s`, `1h2m3s` — the forms YouTube puts in `t` and `start`. */
const TIMESTAMP = /^(?:(\d+)h)?(?:(\d+)m)?(\d+)s?$/;

export function isValidYoutubeUrl(url: string): boolean {
  return YOUTUBE_VIDEO_URL.test(url.trim());
}

/** The 11-character video ID, or null when the URL points at anything else. */
export function getYoutubeVideoId(url: string): string | null {
  const match = YOUTUBE_VIDEO_URL.exec(url.trim());
  return match ? match[1] : null;
}

/**
 * Seconds into the video the link asks to start at, or 0.
 *
 * Shared links routinely carry a timestamp, and dropping it restarts a two-hour talk
 * from the beginning.
 */
export function getYoutubeStartSeconds(url: string): number {
  let query: URLSearchParams;
  try {
    query = new URL(url.trim()).searchParams;
  } catch {
    return 0;
  }

  const raw = (query.get("t") || query.get("start") || "").trim();
  if (!raw) return 0;

  const parts = TIMESTAMP.exec(raw);
  if (!parts) return 0;

  const [, hours, minutes, seconds] = parts;
  return Number(hours || 0) * 3600 + Number(minutes || 0) * 60 + Number(seconds);
}

/**
 * An embeddable player URL, or null when the link is not a single video (a playlist
 * or channel page, say) and so has to open on YouTube instead.
 *
 * Uses youtube-nocookie.com: it serves the same player without setting tracking
 * cookies until playback begins, which is the lighter footing for embedding
 * third-party media in the page rather than sending the visitor to YouTube.
 */
export function buildYoutubeEmbedUrl(url: string): string | null {
  const videoId = getYoutubeVideoId(url);
  if (!videoId) return null;

  const params = new URLSearchParams({
    // The player opens from a deliberate click, so sound-on autoplay is allowed.
    autoplay: "1",
    rel: "0",
  });

  const start = getYoutubeStartSeconds(url);
  if (start > 0) params.set("start", String(start));

  return `https://www.youtube-nocookie.com/embed/${videoId}?${params.toString()}`;
}
